import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useAuthFetch } from '@app/hooks/useAuthFetch';
import { useConnectorsApi } from '@app/hooks/useApis';
import { buildOrgUrl, cloneHeadersWithAuth, resolveBasePath } from '@app/utils/api';
import { normalizeExternalBaseUrl } from '@app/utils/aws-launch';
import { buildCloudFormationQuickCreateUrl } from '@app/utils/aws-console';
import {
  applyAwsTelemetryPreset,
  createDefaultAwsTelemetryForm,
  getEffectiveTelemetryLabCidr,
  getEffectiveTelemetryResolverAllowedCidr,
  validateAwsTelemetryIpv4Cidr,
  type AwsTelemetryDeployForm
} from '@app/utils/aws-telemetry-preset';
import { useConfig } from '@config/provider';

export type AwsTelemetryLauncherProps = {
  initialClusterId?: string;
  initialClusterName?: string;
  initialRegion?: string;
  onCancel?: () => void;
};

type AwsTelemetryMetadata = {
  enabled?: boolean;
  templateUrl?: string;
  defaultRegion?: string;
  allowedRegions?: string[];
};

type AwsTelemetryQuickCreateResponse = {
  url?: string;
  region?: string;
  templateUrl?: string;
  stackName?: string;
  warnings?: string[];
  error?: string;
};

type PreparedManagedConnectors = {
  signature: string;
  clusterId: string;
  connectorAId: string;
  connectorBId: string;
  connectorAName: string;
  connectorBName: string;
  userDataA: string;
  userDataB: string;
};

type AwsTelemetryProgress = {
  percent: number;
  label: string;
};

const sanitizeNameSegment = (value: string): string => {
  const cleaned = value.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'telemetry';
};

const normalizeManagedConnectorKeyName = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const basename = trimmed.replace(/^.*[\\/]/, '');
  return basename.replace(/\.pem$/i, '');
};

const validateAwsLaunchUrl = (url: string): void => {
  if (url.includes('param_ManagedConnectorUserDataA=') || url.includes('param_ManagedConnectorUserDataB=')) {
    throw new Error(
      'Launch URL is using legacy inline managed connector userdata and is too large for AWS sign-in redirect. Redeploy aws-adaptor with rendered-template quick-create support.'
    );
  }
  if (url.length > 7500) {
    throw new Error('Launch URL is too long for reliable AWS sign-in redirect handling. Use aws-adaptor rendered-template quick-create flow.');
  }
};

const AwsTelemetryLauncher = ({ initialClusterId, initialClusterName, initialRegion, onCancel }: AwsTelemetryLauncherProps) => {
  const orgId = useActiveOrgId();
  const connectorsApi = useConnectorsApi();
  const { apiBaseUrl, awsAdaptorBaseUrl, awsTelemetryTemplateUrl, awsTelemetryDefaultRegion } = useConfig();
  const basePath = resolveBasePath(apiBaseUrl);
  const { fetchWithAuthRetry } = useAuthFetch();
  const adaptorBasePath = useMemo(() => normalizeExternalBaseUrl(awsAdaptorBaseUrl), [awsAdaptorBaseUrl]);

  const [awsTelemetryForm, setAwsTelemetryForm] = useState<AwsTelemetryDeployForm>(() => {
    const region = initialRegion?.trim() || awsTelemetryDefaultRegion?.trim() || 'us-east-1';
    const next = createDefaultAwsTelemetryForm(region);
    return initialClusterId ? { ...next, clusterId: initialClusterId } : next;
  });
  const [awsTelemetryLaunchError, setAwsTelemetryLaunchError] = useState<string | null>(null);
  const [awsTelemetryLaunchUrl, setAwsTelemetryLaunchUrl] = useState<string | null>(null);
  const [awsTelemetryCopyStatus, setAwsTelemetryCopyStatus] = useState<string | null>(null);
  const [awsTelemetryProgress, setAwsTelemetryProgress] = useState<AwsTelemetryProgress | null>(null);
  const [awsTelemetrySelectedClusterName, setAwsTelemetrySelectedClusterName] = useState<string>(initialClusterName ?? '');
  const [awsTelemetryMetadata, setAwsTelemetryMetadata] = useState<AwsTelemetryMetadata | null>(null);
  const [preparedManagedConnectors, setPreparedManagedConnectors] = useState<PreparedManagedConnectors | null>(null);
  const [managedConnectorIpPrefillAttempted, setManagedConnectorIpPrefillAttempted] = useState(false);
  const [awsTelemetryWizardStep, setAwsTelemetryWizardStep] = useState<1 | 2>(1);
  const awsTelemetryPresetInputRef = useRef<HTMLInputElement | null>(null);

  const effectiveCreatePrivateHostedZone = awsTelemetryForm.existingPrivateHostedZoneId.trim() ? 'false' : awsTelemetryForm.createPrivateHostedZone;
  const effectiveCreateResolverEndpoint = awsTelemetryForm.existingResolverEndpointId.trim() ? 'false' : awsTelemetryForm.createResolverEndpoint;
  const managedConnectorSignature = useMemo(
    () =>
      JSON.stringify({
        orgId,
        clusterId: awsTelemetryForm.clusterId.trim(),
        amiId: awsTelemetryForm.connectorAmiId.trim(),
        stackName: awsTelemetryForm.stackName.trim(),
        stackNamePrefix: awsTelemetryForm.stackNamePrefix.trim()
      }),
    [awsTelemetryForm.clusterId, awsTelemetryForm.connectorAmiId, awsTelemetryForm.stackName, awsTelemetryForm.stackNamePrefix, orgId]
  );

  const updateAwsTelemetryForm =
    (field: keyof AwsTelemetryDeployForm) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setAwsTelemetryForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  useEffect(() => {
    setAwsTelemetryForm((prev) => {
      if (!initialClusterId || prev.clusterId === initialClusterId) {
        return prev;
      }
      return { ...prev, clusterId: initialClusterId };
    });
  }, [initialClusterId]);

  useEffect(() => {
    if (initialClusterName?.trim()) {
      setAwsTelemetrySelectedClusterName(initialClusterName.trim());
    }
  }, [initialClusterName]);

  useEffect(() => {
    const region = initialRegion?.trim() || awsTelemetryDefaultRegion?.trim();
    if (!region) {
      return;
    }
    setAwsTelemetryForm((prev) => (prev.region.trim() === region ? prev : { ...prev, region }));
  }, [awsTelemetryDefaultRegion, initialRegion]);

  useEffect(() => {
    const useManagedConnectors = Boolean(awsTelemetryForm.connectorAmiId.trim());
    const shouldPrefill =
      useManagedConnectors &&
      awsTelemetryForm.managedConnectorPublicAccess === 'true' &&
      !managedConnectorIpPrefillAttempted &&
      (!awsTelemetryForm.managedConnectorSshCidr.trim() || awsTelemetryForm.managedConnectorSshCidr.trim() === '0.0.0.0/0');

    if (!shouldPrefill) {
      if (!useManagedConnectors || awsTelemetryForm.managedConnectorPublicAccess !== 'true') {
        setManagedConnectorIpPrefillAttempted(false);
      }
      return;
    }

    const controller = new AbortController();
    const lookup = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json', {
          cache: 'no-store',
          signal: controller.signal
        });
        if (!response.ok) {
          return;
        }
        const payload = (await response.json()) as { ip?: string };
        const ip = payload.ip?.trim();
        if (!ip) {
          return;
        }
        setAwsTelemetryForm((prev) => {
          const current = prev.managedConnectorSshCidr.trim();
          if (current && current !== '0.0.0.0/0') {
            return prev;
          }
          return { ...prev, managedConnectorSshCidr: `${ip}/32` };
        });
      } catch {
        // Leave the fallback CIDR in place when lookup fails.
      } finally {
        setManagedConnectorIpPrefillAttempted(true);
      }
    };

    void lookup();
    return () => controller.abort();
  }, [
    awsTelemetryForm.connectorAmiId,
    awsTelemetryForm.managedConnectorPublicAccess,
    awsTelemetryForm.managedConnectorSshCidr,
    managedConnectorIpPrefillAttempted
  ]);

  const parseError = useCallback(async (response: Response): Promise<string> => {
    try {
      const payload = (await response.json()) as { detail?: string; title?: string; error?: string; message?: string };
      return payload.detail || payload.message || payload.title || payload.error || `Request failed with status ${response.status}`;
    } catch {
      return `Request failed with status ${response.status}`;
    }
  }, []);

  const fetchAwsTelemetryMetadata = useCallback(async () => {
    if (!adaptorBasePath) {
      setAwsTelemetryMetadata(null);
      return;
    }

    try {
      const headers = await cloneHeadersWithAuth({
        Accept: 'application/json'
      });
      const response = await fetch(`${adaptorBasePath}/v1/aws/telemetry/metadata`, {
        headers
      });
      if (!response.ok) {
        throw new Error(await parseError(response));
      }
      const payload = (await response.json()) as AwsTelemetryMetadata;
      setAwsTelemetryMetadata(payload);
      setAwsTelemetryForm((prev) => ({
        ...prev,
        region: prev.region.trim() || payload.defaultRegion?.trim() || prev.region
      }));
    } catch (error) {
      setAwsTelemetryMetadata(null);
      if (!awsTelemetryTemplateUrl) {
        setAwsTelemetryLaunchError(error instanceof Error ? error.message : 'Failed to load AWS telemetry metadata.');
      }
    }
  }, [adaptorBasePath, awsTelemetryTemplateUrl, parseError]);

  useEffect(() => {
    void fetchAwsTelemetryMetadata();
  }, [fetchAwsTelemetryMetadata]);

  useEffect(() => {
    setPreparedManagedConnectors((prev) => (prev?.signature === managedConnectorSignature ? prev : null));
  }, [managedConnectorSignature]);

  const resetWizardForm = useCallback(() => {
    setAwsTelemetryForm((prev) => {
      const region = initialRegion?.trim() || awsTelemetryDefaultRegion?.trim() || prev.region;
      const next = createDefaultAwsTelemetryForm(region);
      return initialClusterId ? { ...next, clusterId: initialClusterId } : next;
    });
    setAwsTelemetrySelectedClusterName(initialClusterName ?? '');
    setAwsTelemetryLaunchError(null);
    setAwsTelemetryCopyStatus(null);
    setAwsTelemetryProgress(null);
    setAwsTelemetryLaunchUrl(null);
    setAwsTelemetryWizardStep(1);
  }, [awsTelemetryDefaultRegion, initialClusterId, initialClusterName, initialRegion]);

  const cancelWizard = useCallback(() => {
    resetWizardForm();
    onCancel?.();
  }, [onCancel, resetWizardForm]);

  const validateAwsTelemetryRegion = useCallback(() => {
    const region = awsTelemetryForm.region.trim();
    if (!region) {
      throw new Error('AWS region is required.');
    }
    const allowedRegions = (awsTelemetryMetadata?.allowedRegions ?? []).map((allowedRegion) => allowedRegion.trim()).filter(Boolean);
    if (allowedRegions.length > 0 && !allowedRegions.includes(region)) {
      throw new Error(`AWS region ${region} is not allowed for telemetry launch.`);
    }
  }, [awsTelemetryForm.region, awsTelemetryMetadata?.allowedRegions]);

  const validateAwsTelemetryForm = useCallback(() => {
    validateAwsTelemetryRegion();
    if (!awsTelemetryForm.telemetrySourceCidr.trim()) {
      throw new Error('Telemetry source CIDR is required.');
    }
    validateAwsTelemetryIpv4Cidr(awsTelemetryForm.telemetrySourceCidr, 'Telemetry source CIDR');
    validateAwsTelemetryIpv4Cidr(getEffectiveTelemetryLabCidr(awsTelemetryForm), 'Return route CIDR');
    if (effectiveCreateResolverEndpoint === 'true') {
      validateAwsTelemetryIpv4Cidr(getEffectiveTelemetryResolverAllowedCidr(awsTelemetryForm), 'Resolver allowed CIDR');
    }
    if (!awsTelemetryForm.existingEnsureResourcesRoleArn.trim()) {
      throw new Error('Existing helper IAM role ARN is required.');
    }
    const managedConnectorMode = Boolean(awsTelemetryForm.connectorAmiId.trim());
    if (managedConnectorMode) {
      if (!awsTelemetryForm.clusterId.trim()) {
        throw new Error('Managed connector mode requires a selected cluster. Launch from the cluster row action in Connectivity > Clusters.');
      }
      if (
        awsTelemetryForm.managedConnectorPublicAccess === 'true' &&
        !normalizeManagedConnectorKeyName(awsTelemetryForm.managedConnectorKeyName)
      ) {
        throw new Error('Managed connector key pair is required when Connector AMI ID is set and you want SSH access.');
      }
      if (awsTelemetryForm.connectorInstanceA.trim() || awsTelemetryForm.connectorInstanceB.trim()) {
        throw new Error('Use either connector instance IDs or Connector AMI ID, not both.');
      }
    } else if (!awsTelemetryForm.connectorInstanceA.trim() || !awsTelemetryForm.connectorInstanceB.trim()) {
      throw new Error('Connector instance A and Connector instance B are required unless Connector AMI ID is set.');
    }
    if (awsTelemetryForm.createLabRoutes === 'true') {
      const targets = [
        awsTelemetryForm.transitInstanceId.trim(),
        awsTelemetryForm.transitGatewayId.trim(),
        awsTelemetryForm.transitNetworkInterfaceId.trim()
      ].filter(Boolean);
      if (targets.length > 1) {
        throw new Error('Specify only one route target: Transit instance ID, Transit gateway ID, or Transit network interface ID.');
      }
    }
    if (awsTelemetryForm.existingTelemetryNlbArn.trim()) {
      if (!awsTelemetryForm.existingTelemetryNlbDnsName.trim()) {
        throw new Error('Existing telemetry NLB DNS name is required when reusing an existing telemetry NLB.');
      }
      if (!awsTelemetryForm.existingTelemetryNlbCanonicalHostedZoneId.trim()) {
        throw new Error('Existing telemetry NLB hosted zone ID is required when reusing an existing telemetry NLB.');
      }
    }
  }, [awsTelemetryForm, effectiveCreateResolverEndpoint, validateAwsTelemetryRegion]);

  const validateAwsTelemetryStepOne = useCallback(() => {
    validateAwsTelemetryRegion();
    if (!awsTelemetryForm.stackName.trim()) {
      throw new Error('Stack name is required.');
    }
    if (!awsTelemetryForm.vpcId.trim()) {
      throw new Error('VPC ID is required.');
    }
    if (!awsTelemetryForm.existingEnsureResourcesRoleArn.trim()) {
      throw new Error('Existing helper IAM role ARN is required.');
    }

    const managedConnectorMode = Boolean(awsTelemetryForm.connectorAmiId.trim());
    if (managedConnectorMode) {
      if (!awsTelemetryForm.clusterId.trim()) {
        throw new Error('Managed connector mode requires a selected cluster.');
      }
      if (
        awsTelemetryForm.managedConnectorPublicAccess === 'true' &&
        !normalizeManagedConnectorKeyName(awsTelemetryForm.managedConnectorKeyName)
      ) {
        throw new Error('Managed connector key pair is required when public access is enabled.');
      }
      return;
    }

    if (!awsTelemetryForm.connectorInstanceA.trim() || !awsTelemetryForm.connectorInstanceB.trim()) {
      throw new Error('Connector instance A and Connector instance B are required unless Connector AMI ID is set.');
    }
  }, [awsTelemetryForm, validateAwsTelemetryRegion]);

  const validateAwsTelemetryStepTwo = useCallback(() => {
    if (!awsTelemetryForm.telemetrySourceCidr.trim()) {
      throw new Error('Telemetry source CIDR is required.');
    }
    validateAwsTelemetryForm();
  }, [awsTelemetryForm.telemetrySourceCidr, validateAwsTelemetryForm]);

  const importAwsTelemetryPreset = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    try {
      const raw = JSON.parse(await file.text()) as unknown;
      setAwsTelemetryForm((prev) => applyAwsTelemetryPreset(prev, raw));
      setAwsTelemetryLaunchError(null);
      setAwsTelemetryCopyStatus(`Imported preset: ${file.name}`);
    } catch (error) {
      setAwsTelemetryCopyStatus(null);
      setAwsTelemetryLaunchError(error instanceof Error ? error.message : 'Failed to import AWS telemetry preset.');
    }
  }, []);

  const resetTelemetryConnectorEnrollment = useCallback(
    async (connectorId: string) => {
      if (!orgId) {
        throw new Error('Select an organization first.');
      }
      const headers = await cloneHeadersWithAuth(undefined);
      const response = await fetchWithAuthRetry(buildOrgUrl(basePath, orgId, `connectors/${connectorId}/enrollment:reset`), {
        method: 'POST',
        headers
      });
      if (!response.ok) {
        throw new Error(await parseError(response));
      }
    },
    [basePath, fetchWithAuthRetry, orgId, parseError]
  );

  const fetchTelemetryConnectorUserData = useCallback(
    async (connectorId: string) => {
      if (!orgId) {
        throw new Error('Select an organization first.');
      }
      const response = await connectorsApi.orgsOrgIdConnectorsConnectorIdUserdataGetRaw(
        { orgId, connectorId },
        async ({ init }) => {
          const headers = await cloneHeadersWithAuth(init.headers ?? undefined);
          headers.Accept = 'text/cloud-config';
          return { headers };
        }
      );
      return response.raw.text();
    },
    [connectorsApi, orgId]
  );

  const prepareManagedConnectorBootstrap = useCallback(async (): Promise<PreparedManagedConnectors | null> => {
    if (!awsTelemetryForm.connectorAmiId.trim()) {
      return null;
    }
    if (preparedManagedConnectors?.signature === managedConnectorSignature) {
      return preparedManagedConnectors;
    }
    if (!orgId) {
      throw new Error('Select an organization first.');
    }

    const clusterId = awsTelemetryForm.clusterId.trim();
    if (!clusterId) {
      throw new Error('Managed connector mode requires a selected cluster. Launch from the cluster row action in Connectivity > Clusters.');
    }

    const timestamp = Date.now().toString(36);
    const nameRoot = sanitizeNameSegment(awsTelemetryForm.stackNamePrefix || awsTelemetryForm.stackName || 'telemetry');
    const connectorAName = `${nameRoot}-${timestamp}-a`;
    const connectorBName = `${nameRoot}-${timestamp}-b`;

    setAwsTelemetryCopyStatus(`Preparing managed connectors in cluster ${clusterId}…`);
    setAwsTelemetryProgress({
      percent: 10,
      label: `Preparing managed connectors in cluster ${clusterId}…`
    });

    const connectorA = await connectorsApi.orgsOrgIdConnectorsPost({
      orgId,
      connectorCreate: {
        name: connectorAName,
        clusterId,
        description: 'Managed telemetry connector A',
        labels: {
          managedBy: 'aws-telemetry-launcher',
          clusterId,
          azRole: 'a'
        }
      }
    });
    const connectorAId = connectorA.id?.trim();
    if (!connectorAId) {
      throw new Error('Connector A creation response did not include an ID.');
    }
    setAwsTelemetryProgress({
      percent: 30,
      label: `Created connector A (${connectorAId}). Creating connector B…`
    });

    const connectorB = await connectorsApi.orgsOrgIdConnectorsPost({
      orgId,
      connectorCreate: {
        name: connectorBName,
        clusterId,
        description: 'Managed telemetry connector B',
        labels: {
          managedBy: 'aws-telemetry-launcher',
          clusterId,
          azRole: 'b'
        }
      }
    });
    const connectorBId = connectorB.id?.trim();
    if (!connectorBId) {
      throw new Error('Connector B creation response did not include an ID.');
    }
    setAwsTelemetryProgress({
      percent: 50,
      label: `Created connector B (${connectorBId}). Resetting enrollment…`
    });

    await resetTelemetryConnectorEnrollment(connectorAId);
    setAwsTelemetryProgress({
      percent: 65,
      label: `Reset connector A enrollment. Resetting connector B enrollment…`
    });
    await resetTelemetryConnectorEnrollment(connectorBId);
    setAwsTelemetryProgress({
      percent: 80,
      label: 'Enrollment reset complete. Fetching connector bootstrap data…'
    });

    const [userDataA, userDataB] = await Promise.all([
      fetchTelemetryConnectorUserData(connectorAId),
      fetchTelemetryConnectorUserData(connectorBId)
    ]);

    const prepared = {
      signature: managedConnectorSignature,
      clusterId,
      connectorAId,
      connectorBId,
      connectorAName,
      connectorBName,
      userDataA,
      userDataB
    };
    setPreparedManagedConnectors(prepared);
    setAwsTelemetryProgress({
      percent: 100,
      label: `Prepared managed connectors ${connectorAId} and ${connectorBId}.`
    });
    return prepared;
  }, [
    awsTelemetryForm.connectorAmiId,
    awsTelemetryForm.clusterId,
    awsTelemetryForm.stackName,
    awsTelemetryForm.stackNamePrefix,
    connectorsApi,
    fetchTelemetryConnectorUserData,
    managedConnectorSignature,
    orgId,
    preparedManagedConnectors,
    resetTelemetryConnectorEnrollment
  ]);

  const buildAwsTelemetryUrl = useCallback(
    (managedConnectorOverrides?: Record<string, string | undefined>) => {
      validateAwsTelemetryForm();
      const activeTemplateUrl = awsTelemetryMetadata?.templateUrl?.trim() || awsTelemetryTemplateUrl;
      if (!activeTemplateUrl) {
        throw new Error('AWS telemetry template URL is not configured in runtime-config.');
      }
      const effectiveLabCidr = getEffectiveTelemetryLabCidr(awsTelemetryForm);
      const effectiveResolverAllowedCidr = getEffectiveTelemetryResolverAllowedCidr(awsTelemetryForm);

      return buildCloudFormationQuickCreateUrl({
        region: awsTelemetryForm.region,
        templateUrl: activeTemplateUrl,
        stackName: awsTelemetryForm.stackName,
        parameters: {
          StackNamePrefix: awsTelemetryForm.stackNamePrefix,
          VpcId: awsTelemetryForm.vpcId,
          VpcCidr: awsTelemetryForm.vpcCidr.trim() || undefined,
          PrivateSubnetA: awsTelemetryForm.privateSubnetA.trim() || undefined,
          PrivateSubnetB: awsTelemetryForm.privateSubnetB.trim() || undefined,
          ConnectorInstanceA: awsTelemetryForm.connectorAmiId.trim() ? undefined : awsTelemetryForm.connectorInstanceA,
          ConnectorInstanceB: awsTelemetryForm.connectorAmiId.trim() ? undefined : awsTelemetryForm.connectorInstanceB,
          ConnectorAmiId: awsTelemetryForm.connectorAmiId.trim() || undefined,
          ConnectorInstanceType: awsTelemetryForm.connectorAmiId.trim() ? 't2.medium' : undefined,
          ManagedConnectorKeyName: awsTelemetryForm.connectorAmiId.trim()
            ? normalizeManagedConnectorKeyName(awsTelemetryForm.managedConnectorKeyName) || undefined
            : undefined,
          ManagedConnectorPublicAccess: awsTelemetryForm.connectorAmiId.trim() ? awsTelemetryForm.managedConnectorPublicAccess : undefined,
          ManagedConnectorSshCidr: awsTelemetryForm.connectorAmiId.trim() ? awsTelemetryForm.managedConnectorSshCidr.trim() || undefined : undefined,
          ConnectorSecurityGroupId: awsTelemetryForm.connectorSecurityGroupId.trim() || undefined,
          ExistingTelemetryNlbArn: awsTelemetryForm.existingTelemetryNlbArn,
          ExistingTelemetryNlbDnsName: awsTelemetryForm.existingTelemetryNlbDnsName,
          ExistingTelemetryNlbCanonicalHostedZoneId: awsTelemetryForm.existingTelemetryNlbCanonicalHostedZoneId,
          ExistingSyslogTargetGroupArn: awsTelemetryForm.existingSyslogTargetGroupArn,
          ExistingSnmpTrapTargetGroupArn: awsTelemetryForm.existingSnmpTrapTargetGroupArn,
          ExistingSyslogListenerArn: awsTelemetryForm.existingSyslogListenerArn,
          ExistingSnmpTrapListenerArn: awsTelemetryForm.existingSnmpTrapListenerArn,
          CreateConnectorIngressRules: awsTelemetryForm.createConnectorIngressRules,
          TelemetryHealthCheckPort: awsTelemetryForm.telemetryHealthCheckPort,
          TelemetrySourceCidr: awsTelemetryForm.telemetrySourceCidr,
          PrivateHostedZoneName: effectiveCreatePrivateHostedZone === 'true' ? awsTelemetryForm.privateHostedZoneName : undefined,
          CreatePrivateHostedZone: effectiveCreatePrivateHostedZone,
          ExistingPrivateHostedZoneId: awsTelemetryForm.existingPrivateHostedZoneId,
          TelemetryRecordName: awsTelemetryForm.telemetryRecordName,
          CreateTelemetryAliasRecord: awsTelemetryForm.createTelemetryAliasRecord,
          CreateResolverEndpoint: effectiveCreateResolverEndpoint,
          ResolverAllowedCidr: effectiveCreateResolverEndpoint === 'true' ? effectiveResolverAllowedCidr : undefined,
          ExistingResolverEndpointId: awsTelemetryForm.existingResolverEndpointId,
          ExistingEnsureResourcesRoleArn: awsTelemetryForm.existingEnsureResourcesRoleArn,
          CreateLabRoutes: awsTelemetryForm.createLabRoutes,
          LabCidr: effectiveLabCidr,
          TransitInstanceId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.transitInstanceId : undefined,
          TransitGatewayId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.transitGatewayId : undefined,
          TransitNetworkInterfaceId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.transitNetworkInterfaceId : undefined,
          LabTransitPrivateIp: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.labTransitPrivateIp.trim() || undefined : undefined,
          RouteTableAId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.routeTableAId : undefined,
          RouteTableBId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.routeTableBId : undefined,
          ...managedConnectorOverrides
        }
      });
    },
    [awsTelemetryForm, awsTelemetryMetadata?.templateUrl, awsTelemetryTemplateUrl, effectiveCreatePrivateHostedZone, effectiveCreateResolverEndpoint, validateAwsTelemetryForm]
  );

  const buildAwsTelemetryRequestPayload = useCallback(
    (managedConnectorOverrides?: Record<string, string | undefined>) => {
      validateAwsTelemetryForm();
      const effectiveLabCidr = getEffectiveTelemetryLabCidr(awsTelemetryForm);
      const effectiveResolverAllowedCidr = getEffectiveTelemetryResolverAllowedCidr(awsTelemetryForm);
      return {
        region: awsTelemetryForm.region,
        stackName: awsTelemetryForm.stackName,
        parameters: {
          StackNamePrefix: awsTelemetryForm.stackNamePrefix,
          VpcId: awsTelemetryForm.vpcId,
          VpcCidr: awsTelemetryForm.vpcCidr.trim() || undefined,
          PrivateSubnetA: awsTelemetryForm.privateSubnetA.trim() || undefined,
          PrivateSubnetB: awsTelemetryForm.privateSubnetB.trim() || undefined,
          ConnectorInstanceA: awsTelemetryForm.connectorAmiId.trim() ? undefined : awsTelemetryForm.connectorInstanceA,
          ConnectorInstanceB: awsTelemetryForm.connectorAmiId.trim() ? undefined : awsTelemetryForm.connectorInstanceB,
          ConnectorAmiId: awsTelemetryForm.connectorAmiId.trim() || undefined,
          ConnectorInstanceType: awsTelemetryForm.connectorAmiId.trim() ? 't2.medium' : undefined,
          ManagedConnectorKeyName: awsTelemetryForm.connectorAmiId.trim()
            ? normalizeManagedConnectorKeyName(awsTelemetryForm.managedConnectorKeyName) || undefined
            : undefined,
          ManagedConnectorPublicAccess: awsTelemetryForm.connectorAmiId.trim() ? awsTelemetryForm.managedConnectorPublicAccess : undefined,
          ManagedConnectorSshCidr: awsTelemetryForm.connectorAmiId.trim() ? awsTelemetryForm.managedConnectorSshCidr.trim() || undefined : undefined,
          ConnectorSecurityGroupId: awsTelemetryForm.connectorSecurityGroupId.trim() || undefined,
          ExistingTelemetryNlbArn: awsTelemetryForm.existingTelemetryNlbArn,
          ExistingTelemetryNlbDnsName: awsTelemetryForm.existingTelemetryNlbDnsName,
          ExistingTelemetryNlbCanonicalHostedZoneId: awsTelemetryForm.existingTelemetryNlbCanonicalHostedZoneId,
          ExistingSyslogTargetGroupArn: awsTelemetryForm.existingSyslogTargetGroupArn,
          ExistingSnmpTrapTargetGroupArn: awsTelemetryForm.existingSnmpTrapTargetGroupArn,
          ExistingSyslogListenerArn: awsTelemetryForm.existingSyslogListenerArn,
          ExistingSnmpTrapListenerArn: awsTelemetryForm.existingSnmpTrapListenerArn,
          CreateConnectorIngressRules: awsTelemetryForm.createConnectorIngressRules,
          TelemetryHealthCheckPort: awsTelemetryForm.telemetryHealthCheckPort,
          TelemetrySourceCidr: awsTelemetryForm.telemetrySourceCidr,
          PrivateHostedZoneName: effectiveCreatePrivateHostedZone === 'true' ? awsTelemetryForm.privateHostedZoneName : undefined,
          CreatePrivateHostedZone: effectiveCreatePrivateHostedZone,
          ExistingPrivateHostedZoneId: awsTelemetryForm.existingPrivateHostedZoneId,
          TelemetryRecordName: awsTelemetryForm.telemetryRecordName,
          CreateTelemetryAliasRecord: awsTelemetryForm.createTelemetryAliasRecord,
          CreateResolverEndpoint: effectiveCreateResolverEndpoint,
          ResolverAllowedCidr: effectiveCreateResolverEndpoint === 'true' ? effectiveResolverAllowedCidr : undefined,
          ExistingResolverEndpointId: awsTelemetryForm.existingResolverEndpointId,
          ExistingEnsureResourcesRoleArn: awsTelemetryForm.existingEnsureResourcesRoleArn,
          CreateLabRoutes: awsTelemetryForm.createLabRoutes,
          LabCidr: effectiveLabCidr,
          TransitInstanceId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.transitInstanceId : undefined,
          TransitGatewayId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.transitGatewayId : undefined,
          TransitNetworkInterfaceId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.transitNetworkInterfaceId : undefined,
          LabTransitPrivateIp: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.labTransitPrivateIp.trim() || undefined : undefined,
          RouteTableAId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.routeTableAId : undefined,
          RouteTableBId: awsTelemetryForm.createLabRoutes === 'true' ? awsTelemetryForm.routeTableBId : undefined,
          ...managedConnectorOverrides
        }
      };
    },
    [awsTelemetryForm, effectiveCreatePrivateHostedZone, effectiveCreateResolverEndpoint, validateAwsTelemetryForm]
  );

  const fetchAwsTelemetryLaunchUrl = useCallback(async () => {
    const managedMode = Boolean(awsTelemetryForm.connectorAmiId.trim());
    const managedConnectorOverrides = awsTelemetryForm.connectorAmiId.trim()
      ? await prepareManagedConnectorBootstrap().then((prepared) =>
          prepared
            ? {
                ManagedConnectorDisplayNameA: prepared.connectorAName,
                ManagedConnectorDisplayNameB: prepared.connectorBName,
                ManagedConnectorUserDataA: prepared.userDataA,
                ManagedConnectorUserDataB: prepared.userDataB
              }
            : undefined
        )
      : undefined;

    if (!adaptorBasePath) {
      if (managedMode) {
        throw new Error('Managed connector launch requires aws-adaptor quick-create URL API. Configure awsAdaptorBaseUrl and retry.');
      }
      return buildAwsTelemetryUrl(managedConnectorOverrides);
    }

    try {
      const headers = await cloneHeadersWithAuth({
        'Content-Type': 'application/json',
        Accept: 'application/json'
      });
      const response = await fetchWithAuthRetry(`${adaptorBasePath}/v1/aws/telemetry/quick-create-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify(buildAwsTelemetryRequestPayload(managedConnectorOverrides))
      });
      if (!response.ok) {
        throw new Error(await parseError(response));
      }
      const payload = (await response.json()) as AwsTelemetryQuickCreateResponse;
      if (!payload.url) {
        throw new Error('AWS adaptor response missing quick-create URL.');
      }
      validateAwsLaunchUrl(payload.url);
      return payload.url;
    } catch (error) {
      if (managedConnectorOverrides && Object.keys(managedConnectorOverrides).length > 0) {
        throw error;
      }
      if (awsTelemetryMetadata?.templateUrl?.trim() || awsTelemetryTemplateUrl) {
        return buildAwsTelemetryUrl(managedConnectorOverrides);
      }
      throw error;
    }
  }, [
    adaptorBasePath,
    awsTelemetryForm.connectorAmiId,
    awsTelemetryMetadata?.templateUrl,
    awsTelemetryTemplateUrl,
    buildAwsTelemetryRequestPayload,
    buildAwsTelemetryUrl,
    fetchWithAuthRetry,
    parseError,
    prepareManagedConnectorBootstrap
  ]);

  const launchAwsTelemetryStack = useCallback(async () => {
    try {
      const url = await fetchAwsTelemetryLaunchUrl();
      setAwsTelemetryLaunchUrl(url);
      setAwsTelemetryLaunchError(null);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setAwsTelemetryProgress(null);
      setAwsTelemetryLaunchError(error instanceof Error ? error.message : 'Failed to build AWS CloudFormation launch URL.');
    }
  }, [fetchAwsTelemetryLaunchUrl]);

  const copyAwsTelemetryLaunchUrl = useCallback(async () => {
    try {
      const url = await fetchAwsTelemetryLaunchUrl();
      await navigator.clipboard.writeText(url);
      setAwsTelemetryLaunchUrl(url);
      setAwsTelemetryLaunchError(null);
      setAwsTelemetryCopyStatus('Quick-create link copied.');
      setAwsTelemetryProgress(null);
    } catch (error) {
      setAwsTelemetryProgress(null);
      setAwsTelemetryCopyStatus(null);
      setAwsTelemetryLaunchError(error instanceof Error ? error.message : 'Failed to copy AWS CloudFormation quick-create URL.');
    }
  }, [fetchAwsTelemetryLaunchUrl]);

  const showLabRouteInputs = awsTelemetryForm.createLabRoutes === 'true';
  const useManagedConnectors = Boolean(awsTelemetryForm.connectorAmiId.trim());
  const canLaunch = Boolean(awsAdaptorBaseUrl || awsTelemetryMetadata?.templateUrl || awsTelemetryTemplateUrl);
  const stepTitle = awsTelemetryWizardStep === 1 ? 'Step 1 of 2: Core configuration' : 'Step 2 of 2: Review deployment options';
  const canContinueToStepTwo = useMemo(() => {
    try {
      validateAwsTelemetryStepOne();
      return true;
    } catch {
      return false;
    }
  }, [validateAwsTelemetryStepOne]);
  const canSubmitWizard = useMemo(() => {
    if (!canLaunch) {
      return false;
    }
    try {
      validateAwsTelemetryStepOne();
      validateAwsTelemetryStepTwo();
      return true;
    } catch {
      return false;
    }
  }, [canLaunch, validateAwsTelemetryStepOne, validateAwsTelemetryStepTwo]);

  return (
    <div className="org-settings-form aws-telemetry-form">
      <p className="form-field__hint aws-telemetry-form__full" style={{ margin: 0 }}>
        {awsAdaptorBaseUrl
          ? 'Template URLs are generated securely by aws-adaptor at launch time. The stack can either reuse existing connector instances or launch two managed ones from a supplied AMI. In managed mode the launcher creates two connectors in the selected existing cluster, resets enrollment, fetches connector bootstrap cloud-config, and injects it into the EC2 launch.'
          : 'This launcher uses the configured runtime CloudFormation template URL. The stack can either reuse existing connector instances or launch two managed ones from a supplied AMI. In managed mode the launcher creates two connectors in the selected existing cluster, resets enrollment, fetches connector bootstrap cloud-config, and injects it into the EC2 launch.'}
      </p>
      <p className="form-field__hint aws-telemetry-form__full" style={{ margin: 0, fontWeight: 600 }}>
        {stepTitle}
      </p>
      <div className="aws-telemetry-form__full" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button type="button" className="chip-button" onClick={() => awsTelemetryPresetInputRef.current?.click()}>
          Import Preset JSON
        </button>
        <button
          type="button"
          className="chip-button"
          onClick={resetWizardForm}
        >
          Reset Defaults
        </button>
        <input
          ref={awsTelemetryPresetInputRef}
          type="file"
          accept="application/json,.json"
          style={{ display: 'none' }}
          onChange={(event) => void importAwsTelemetryPreset(event)}
        />
      </div>
      <p className="form-field__hint aws-telemetry-form__full">Import a saved telemetry preset JSON, then review the populated values before opening AWS CloudFormation.</p>
      {awsTelemetryWizardStep === 1 ? (
        <>
          <div className="aws-telemetry-form__full" style={{ display: 'grid', gap: '1rem' }}>
            <section>
              <h3 style={{ margin: '0 0 0.75rem' }}>Deployment identity</h3>
              <div className="org-settings-form">
                <label>
                  <span>Stack name</span>
                  <input type="text" value={awsTelemetryForm.stackName} onChange={updateAwsTelemetryForm('stackName')} placeholder="reap-telemetry" />
                  <span className="form-field__hint">CloudFormation stack name. Change this if you want multiple telemetry stacks in the same account/region.</span>
                </label>
                <label>
                  <span>AWS region</span>
                  <input type="text" value={awsTelemetryForm.region} onChange={updateAwsTelemetryForm('region')} placeholder="us-east-1" />
                  <span className="form-field__hint">CloudFormation opens in this AWS region. Use the region that contains the target VPC and connector instances.</span>
                </label>
                <label>
                  <span>VPC ID</span>
                  <input type="text" value={awsTelemetryForm.vpcId} onChange={updateAwsTelemetryForm('vpcId')} placeholder="vpc-xxxxxxxx" />
                </label>
                <label>
                  <span>Helper IAM role ARN</span>
                  <input
                    type="text"
                    value={awsTelemetryForm.existingEnsureResourcesRoleArn}
                    onChange={updateAwsTelemetryForm('existingEnsureResourcesRoleArn')}
                    placeholder="arn:aws:iam::<account-id>:role/reap-telemetry-helper-role"
                  />
                  <span className="form-field__hint">
                    Required. The stack uses this Lambda execution role for network inference and Route53/EC2 custom-resource work.
                  </span>
                </label>
              </div>
            </section>
            <section>
              <h3 style={{ margin: '0 0 0.75rem' }}>Connector deployment</h3>
              <div className="org-settings-form">
                <label>
                  <span>Connector AMI ID</span>
                  <input type="text" value={awsTelemetryForm.connectorAmiId} onChange={updateAwsTelemetryForm('connectorAmiId')} placeholder="ami-xxxxxxxx" />
                  <span className="form-field__hint">
                    Required for a new connector cluster. When set, the stack launches two managed connector instances in separate AZs and ignores connector instance A/B.
                    Leave blank only when reusing existing EC2 connector instances.
                  </span>
                </label>
                {useManagedConnectors ? (
                  <>
                    <label>
                      <span>Selected cluster</span>
                      <input type="text" value={awsTelemetrySelectedClusterName || awsTelemetryForm.clusterId || 'Not selected'} readOnly />
                      <span className="form-field__hint">
                        This is taken from the cluster row action in Connectivity &gt; Clusters. The launcher creates two connectors in this cluster,
                        so attached sites on that cluster are used for telemetry workflows.
                      </span>
                    </label>
                    <label>
                      <span>Managed connector key pair</span>
                      <input type="text" value={awsTelemetryForm.managedConnectorKeyName} onChange={updateAwsTelemetryForm('managedConnectorKeyName')} placeholder="my-ec2-keypair" />
                      <span className="form-field__hint">
                        Required for SSH access. You can enter either the key pair name (for example <code>topo2-key</code>) or a pem filename/path
                        (for example <code>topo2-key.pem</code>); the launcher normalizes it to EC2 <code>KeyName</code>.
                      </span>
                    </label>
                    <label>
                      <span>Managed connector public access</span>
                      <select className="select" value={awsTelemetryForm.managedConnectorPublicAccess} onChange={updateAwsTelemetryForm('managedConnectorPublicAccess')}>
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                      <span className="form-field__hint">
                        Defaults to <code>true</code>. When enabled, the auto-created managed connector subnets get public IPs and direct Internet Gateway routing.
                      </span>
                    </label>
                    <label>
                      <span>Managed connector SSH CIDR</span>
                      <input type="text" value={awsTelemetryForm.managedConnectorSshCidr} onChange={updateAwsTelemetryForm('managedConnectorSshCidr')} placeholder="0.0.0.0/0" />
                      <span className="form-field__hint">
                        Restrict this if possible. It controls inbound SSH access to the managed connector instances on port <code>22</code>.
                      </span>
                    </label>
                  </>
                ) : (
                  <>
                    <label>
                      <span>Connector instance A</span>
                      <input type="text" value={awsTelemetryForm.connectorInstanceA} onChange={updateAwsTelemetryForm('connectorInstanceA')} placeholder="i-aaaa" />
                    </label>
                    <label>
                      <span>Connector instance B</span>
                      <input type="text" value={awsTelemetryForm.connectorInstanceB} onChange={updateAwsTelemetryForm('connectorInstanceB')} placeholder="i-bbbb" />
                    </label>
                  </>
                )}
              </div>
            </section>
          </div>
          <div className="aws-telemetry-form__full" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button type="button" className="shortcut-button" onClick={() => setAwsTelemetryWizardStep(2)} disabled={!canContinueToStepTwo}>
              Next
            </button>
            <button type="button" className="chip-button" onClick={cancelWizard}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="aws-telemetry-form__full" style={{ display: 'grid', gap: '1rem' }}>
            <section>
              <h3 style={{ margin: '0 0 0.75rem' }}>Routing</h3>
              <div className="org-settings-form">
                <label>
                  <span>Telemetry source CIDR</span>
                  <input type="text" value={awsTelemetryForm.telemetrySourceCidr} onChange={updateAwsTelemetryForm('telemetrySourceCidr')} placeholder="10.10.0.0/24" />
                </label>
                <label>
                  <span>Create lab return routes</span>
                  <select className="select" value={awsTelemetryForm.createLabRoutes} onChange={updateAwsTelemetryForm('createLabRoutes')}>
                    <option value="false">false</option>
                    <option value="true">true</option>
                  </select>
                </label>
                {showLabRouteInputs && (
                  <>
                    <label>
                      <span>Return route CIDR</span>
                      <input type="text" value={awsTelemetryForm.labCidr} onChange={updateAwsTelemetryForm('labCidr')} placeholder="Defaults to Telemetry source CIDR" />
                      <span className="form-field__hint">
                        Optional. Set only when the network that needs return routes differs from the telemetry source CIDR.
                      </span>
                    </label>
                    <label>
                      <span>Transit instance ID</span>
                      <input type="text" value={awsTelemetryForm.transitInstanceId} onChange={updateAwsTelemetryForm('transitInstanceId')} placeholder="Optional. Leave blank to auto-detect an existing route target" />
                    </label>
                    <label>
                      <span>Transit gateway ID</span>
                      <input type="text" value={awsTelemetryForm.transitGatewayId} onChange={updateAwsTelemetryForm('transitGatewayId')} placeholder="Optional alternative to transit instance" />
                    </label>
                    <label>
                      <span>Transit network interface ID</span>
                      <input type="text" value={awsTelemetryForm.transitNetworkInterfaceId} onChange={updateAwsTelemetryForm('transitNetworkInterfaceId')} placeholder="Optional ENI route target (eni-xxxx)" />
                      <span className="form-field__hint">
                        Leave all route target fields blank to auto-detect from existing routes, instances tagged <code>Role=lab-transit</code>, or a single
                        source-dest-check-disabled transit instance.
                      </span>
                    </label>
                  </>
                )}
              </div>
            </section>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Advanced</summary>
              <div className="org-settings-form" style={{ marginTop: '1rem' }}>
                <p className="form-field__hint aws-telemetry-form__full" style={{ margin: 0 }}>
                  DNS and resolver options, existing telemetry resources, and other low-level overrides are optional. Expand this section only when
                  you need to reuse or override AWS resources instead of letting the launcher infer them.
                </p>
                <label>
                  <span>Existing private hosted zone ID</span>
                  <input type="text" value={awsTelemetryForm.existingPrivateHostedZoneId} onChange={updateAwsTelemetryForm('existingPrivateHostedZoneId')} placeholder="Leave blank to create a new private zone" />
                  <span className="form-field__hint">If set, the stack reuses this private hosted zone instead of creating a new one.</span>
                </label>
                <label>
                  <span>Existing resolver endpoint ID</span>
                  <input type="text" value={awsTelemetryForm.existingResolverEndpointId} onChange={updateAwsTelemetryForm('existingResolverEndpointId')} placeholder="Leave blank to create a new resolver endpoint" />
                  <span className="form-field__hint">If set, the stack reuses the resolver endpoint and skips creating a new one.</span>
                </label>
                <label>
                  <span>Existing telemetry NLB ARN</span>
                  <input type="text" value={awsTelemetryForm.existingTelemetryNlbArn} onChange={updateAwsTelemetryForm('existingTelemetryNlbArn')} placeholder="Leave blank to create a new NLB" />
                  <span className="form-field__hint">If set, the stack reuses the NLB and skips creating a new one.</span>
                </label>
                <label>
                  <span>Existing telemetry NLB DNS name</span>
                  <input type="text" value={awsTelemetryForm.existingTelemetryNlbDnsName} onChange={updateAwsTelemetryForm('existingTelemetryNlbDnsName')} placeholder="Required when reusing an existing NLB" />
                </label>
                <label>
                  <span>Existing telemetry NLB hosted zone ID</span>
                  <input type="text" value={awsTelemetryForm.existingTelemetryNlbCanonicalHostedZoneId} onChange={updateAwsTelemetryForm('existingTelemetryNlbCanonicalHostedZoneId')} placeholder="Required when reusing an existing NLB" />
                </label>
                <label>
                  <span>Existing syslog target group ARN</span>
                  <input type="text" value={awsTelemetryForm.existingSyslogTargetGroupArn} onChange={updateAwsTelemetryForm('existingSyslogTargetGroupArn')} placeholder="Leave blank to create a new syslog target group" />
                </label>
                <label>
                  <span>Existing SNMP trap target group ARN</span>
                  <input type="text" value={awsTelemetryForm.existingSnmpTrapTargetGroupArn} onChange={updateAwsTelemetryForm('existingSnmpTrapTargetGroupArn')} placeholder="Leave blank to create a new SNMP trap target group" />
                </label>
                <label>
                  <span>Existing syslog listener ARN</span>
                  <input type="text" value={awsTelemetryForm.existingSyslogListenerArn} onChange={updateAwsTelemetryForm('existingSyslogListenerArn')} placeholder="Optional. Set only if UDP/514 listener already exists." />
                </label>
                <label>
                  <span>Existing SNMP trap listener ARN</span>
                  <input type="text" value={awsTelemetryForm.existingSnmpTrapListenerArn} onChange={updateAwsTelemetryForm('existingSnmpTrapListenerArn')} placeholder="Optional. Set only if UDP/162 listener already exists." />
                </label>
                <p className="form-field__hint aws-telemetry-form__full" style={{ margin: 0 }}>
                  Region, stack naming, health-check port, Route53 names, resolver allowlist, route-target lookup, route-table lookup, and connector
                  subnet details are all defaulted or inferred automatically. Only override the fields below when you need to reuse existing AWS resources.
                </p>
                <label>
                  <span>Create connector ingress rules</span>
                  <select className="select" value={awsTelemetryForm.createConnectorIngressRules} onChange={updateAwsTelemetryForm('createConnectorIngressRules')}>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                  <span className="form-field__hint">Set false if equivalent connector SG rules already exist.</span>
                </label>
                <p className="form-field__hint aws-telemetry-form__full" style={{ margin: 0 }}>
                  VPC CIDR, connector subnets, and the shared connector security group are inferred automatically. Older presets may still supply explicit
                  overrides for those values if needed.
                </p>
              </div>
            </details>
          </div>
        </>
      )}
      {awsTelemetryLaunchError && (
        <p className="org-shell__banner aws-telemetry-form__full" role="alert">
          {awsTelemetryLaunchError}
        </p>
      )}
      {awsTelemetryProgress && (
        <div className="discovery-events__progress aws-telemetry-form__full" aria-live="polite">
          <div className="discovery-events__progress-track" aria-hidden="true">
            <div className="discovery-events__progress-fill" style={{ width: `${awsTelemetryProgress.percent}%` }} />
          </div>
          <p className="discovery-events__progress-label">{awsTelemetryProgress.label}</p>
        </div>
      )}
      {awsTelemetryCopyStatus && <p className="helper-text aws-telemetry-form__full">{awsTelemetryCopyStatus}</p>}
      {awsTelemetryLaunchUrl && (
        <label className="aws-telemetry-form__full">
          <span>Generated Quick Create URL</span>
          <input type="text" value={awsTelemetryLaunchUrl} readOnly />
        </label>
      )}
      <p className="form-field__hint aws-telemetry-form__full">
        This opens AWS CloudFormation in a new tab. If the customer already has an AWS Console session, the stack page loads directly with
        the template URL and parameter values prefilled.
      </p>
      {awsTelemetryWizardStep === 2 && (
        <div className="aws-telemetry-form__full" style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button type="button" className="chip-button" onClick={() => setAwsTelemetryWizardStep(1)}>
            Back
          </button>
          <button
            type="button"
            className="shortcut-button"
            onClick={() => void launchAwsTelemetryStack()}
            disabled={!canSubmitWizard}
          >
            Deploy in AWS
          </button>
          <button
            type="button"
            className="chip-button"
            onClick={() => void copyAwsTelemetryLaunchUrl()}
            disabled={!canSubmitWizard}
          >
            Copy Quick Create Link
          </button>
          <button type="button" className="chip-button" onClick={cancelWizard}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default AwsTelemetryLauncher;
